import {
  ACCOUNT_STATUSES,
  type AccountStatusSnapshot,
} from '@src/models/interfaces/accountStatus.interface';

import { DAY_MS } from '../constants';

export type AccountBannerKind = 'trial' | 'pastDue' | 'readOnly' | 'suspended';

export interface AccountBanner {
  kind: AccountBannerKind;
  type: 'info' | 'warning' | 'error';
  messageId: string;
  values?: Record<string, number | Date>;
}

/**
 * Which notice, if any, the account status calls for. Trial and payment notices only go to
 * billing contacts (owner/admin); read-only and suspended go to everyone, since every role is
 * affected. A trial without an end date shows nothing: that is an organization the backfill has
 * not reached yet, not a real trial.
 */
export const getAccountBanner = (
  snapshot: AccountStatusSnapshot,
  isBillingContact: boolean,
  now: number,
): AccountBanner | null => {
  switch (snapshot.status) {
    case ACCOUNT_STATUSES.TRIALING: {
      if (!isBillingContact || !snapshot.trialEndsAt) return null;
      const days = Math.max(0, Math.ceil((snapshot.trialEndsAt.getTime() - now) / DAY_MS));
      return {
        kind: 'trial',
        type: 'info',
        messageId: 'accountStatus.banner.trial',
        values: { days },
      };
    }
    case ACCOUNT_STATUSES.PAST_DUE:
      if (!isBillingContact) return null;
      return snapshot.graceEndsAt
        ? {
            kind: 'pastDue',
            type: 'warning',
            messageId: 'accountStatus.banner.pastDue',
            values: { date: snapshot.graceEndsAt },
          }
        : { kind: 'pastDue', type: 'warning', messageId: 'accountStatus.banner.pastDue.noDate' };
    case ACCOUNT_STATUSES.READ_ONLY:
      return { kind: 'readOnly', type: 'error', messageId: 'accountStatus.banner.readOnly' };
    case ACCOUNT_STATUSES.SUSPENDED:
      return { kind: 'suspended', type: 'error', messageId: 'accountStatus.banner.suspended' };
    default:
      return null;
  }
};
